import React, { useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import "./vehicleMap.css";

const AdminVehicleMap = forwardRef(({ vehicles, selectedVehicle, onVehicleSelect }, ref) => {

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const olaMapsRef = useRef(null);

  const markersRef = useRef({});
  const previousPositionsRef = useRef({});

  // Expose focusOnVehicles method to parent
  useImperativeHandle(ref, () => ({
    focusOnVehicles: (vehiclesToFocus) => {
      const map = mapInstanceRef.current;
      if (!map || !vehiclesToFocus || vehiclesToFocus.length === 0) return;

      // Calculate bounds for all vehicles
      let minLng = 180;
      let maxLng = -180;
      let minLat = 90;
      let maxLat = -90;

      vehiclesToFocus.forEach((vehicle) => {
        if (vehicle.longitude && vehicle.latitude) {
          minLng = Math.min(minLng, vehicle.longitude);
          maxLng = Math.max(maxLng, vehicle.longitude);
          minLat = Math.min(minLat, vehicle.latitude);
          maxLat = Math.max(maxLat, vehicle.latitude);
        }
      });

      // If only one vehicle or all vehicles at same location
      if (minLng === maxLng && minLat === maxLat) {
        map.flyTo({
          center: [minLng, minLat],
          zoom: 12,
          duration: 1000
        });
      } else {
        // Fit bounds with padding
        const bounds = [[minLng, minLat], [maxLng, maxLat]];
        map.fitBounds(bounds, { padding: 100, duration: 1000 });
      }
    }
  }));

  // Smooth animation
  const animateMarker = (marker, from, to, duration = 1500) => {

    const start = performance.now();

    function frame(time) {

      const progress = Math.min((time - start) / duration, 1);

      const lng = from[0] + (to[0] - from[0]) * progress;
      const lat = from[1] + (to[1] - from[1]) * progress;

      marker.setLngLat([lng, lat]);

      if (progress < 1) requestAnimationFrame(frame);

    }

    requestAnimationFrame(frame);
  };

  // Initialize map
  useEffect(() => {

    let mounted = true;

    const loadMap = async () => {

      const { OlaMaps } = await import("olamaps-web-sdk");

      if (!mounted) return;

      const olaMaps = new OlaMaps({
        apiKey: process.env.REACT_APP_OLA_API_KEY
      });

      const map = olaMaps.init({
        container: mapRef.current,
        style:
          "https://api.olamaps.io/tiles/vector/v1/styles/default-light-standard/style.json",
        center: [77.23, 28.61],
        zoom: 5
      });

      mapInstanceRef.current = map;
      olaMapsRef.current = olaMaps;

      map.on("load", () => {
        console.log("Fleet map ready");
      });

    };

    loadMap();

    return () => {
      mounted = false;
    };

  }, []);

  // Marker update
  useEffect(() => {

    const map = mapInstanceRef.current;
    const olaMaps = olaMapsRef.current;

    if (!map || !olaMaps || !vehicles) return;

    // Remove old markers
    Object.keys(markersRef.current).forEach(id => {

      if (!vehicles.find(v => v.vehicleregnumber === id)) {

        markersRef.current[id].remove();
        delete markersRef.current[id];
        delete previousPositionsRef.current[id];

      }

    });

    vehicles.forEach(vehicle => {

      const id = vehicle.vehicleregnumber;
      const lng = vehicle.longitude;
      const lat = vehicle.latitude;

      if (!lng || !lat) return;

      const isMoving = vehicle.speed > 0;

      // Existing marker
      if (markersRef.current[id]) {

        const marker = markersRef.current[id];
        const prev = previousPositionsRef.current[id];

        if (prev) {
          animateMarker(marker, prev, [lng, lat]);
        } else {
          marker.setLngLat([lng, lat]);
        }

        previousPositionsRef.current[id] = [lng, lat];

        const element = marker.getElement();

        element.classList.remove("moving", "stopped");
        element.classList.add(isMoving ? "moving" : "stopped");

        // Rotate vehicle
        const img = element.querySelector("img");

        if (img && vehicle.heading !== undefined) {
          img.style.transform = `rotate(${vehicle.heading}deg)`;
        }

      } else {

        // Create marker
        const el = document.createElement("div");
        el.className = `marker-container ${isMoving ? "moving" : "stopped"}`;

        const img = document.createElement("img");

        img.src =
          "https://claimpro-dev.s3.ap-south-1.amazonaws.com/Assets/crane/craneonmap.png";

        el.appendChild(img);

        const label = document.createElement("div");
        label.className = "marker-label";
        label.innerText = id;

        el.appendChild(label);

        el.onclick = (e) => {

          e.stopPropagation();

          if (onVehicleSelect) {
            onVehicleSelect(vehicle);
          }

        };

        const marker = olaMaps
          .addMarker({
            element: el,
            anchor: "center"
          })
          .setLngLat([lng, lat])
          .addTo(map);

        markersRef.current[id] = marker;
        previousPositionsRef.current[id] = [lng, lat];

      }

    });

  }, [vehicles]);

  // Zoom to selected vehicle
  useEffect(() => {

    const map = mapInstanceRef.current;

    if (!map || !selectedVehicle) return;

    map.flyTo({
      center: [selectedVehicle.longitude, selectedVehicle.latitude],
      zoom: 14,
      duration: 1000
    });

  }, [selectedVehicle]);

  return (
    <div
      ref={mapRef}
      style={{
        width: "100%",
        height: "100%",
        flex: 1
      }}
    />
  );
});

AdminVehicleMap.displayName = "AdminVehicleMap";
export default AdminVehicleMap;